import { useMemo } from 'react';
import { queryOptions, useQueries, useQuery } from '@tanstack/react-query';
import { api, type ProjectRoleResponse } from '@/api/api';

export const projectKeys = {
  all: ['projects'] as const,
  list: () => [...projectKeys.all, 'list'] as const,
  detail: (projectId: string) => [...projectKeys.all, 'detail', projectId] as const,
  members: (projectId: string) => [...projectKeys.all, projectId, 'members'] as const,
  tags: (projectId: string) => [...projectKeys.all, projectId, 'tags'] as const,
  epics: (projectId: string) => [...projectKeys.all, projectId, 'epics'] as const,
  roles: (projectId: string) => [...projectKeys.all, projectId, 'roles'] as const,
};

export const projectsListOptions = () =>
  queryOptions({
    queryKey: projectKeys.list(),
    queryFn: () => api.listProjects(),
  });

export const projectMembersOptions = (projectId: string) =>
  queryOptions({
    queryKey: projectKeys.members(projectId),
    queryFn: () => api.listMembers(projectId),
  });

export const projectTagsOptions = (projectId: string) =>
  queryOptions({
    queryKey: projectKeys.tags(projectId),
    queryFn: () => api.listProjectTags(projectId),
  });

export const projectEpicsOptions = (projectId: string) =>
  queryOptions({
    queryKey: projectKeys.epics(projectId),
    queryFn: () => api.listProjectEpics(projectId),
  });

/**
 * The project's role catalog. Roles are per-project data: a role's `name` is
 * the label to render (member list, status-group headers) and its
 * `permission_group` is what decides capabilities — never the name. Archived
 * roles are omitted, so a status or member may still carry a `role_id` that
 * isn't in this list; always tolerate a miss.
 */
export const projectRolesOptions = (projectId: string) =>
  queryOptions({
    queryKey: projectKeys.roles(projectId),
    queryFn: () => api.listProjectRoles(projectId),
    staleTime: 60_000,
  });

/**
 * Role catalog in display order (`sort_order`, name as tiebreak) plus a
 * `byId` lookup for resolving a `role_id` on a status or member to its role.
 */
export function useProjectRoles(projectId: string) {
  const { data, isPending } = useQuery(projectRolesOptions(projectId));
  const sorted = useMemo(() => {
    const roles = [...(data ?? [])].sort(
      (a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name),
    );
    const byId = new Map<string, ProjectRoleResponse>(roles.map((r) => [r.id, r]));
    return { roles, byId };
  }, [data]);
  return { ...sorted, isPending };
}

/**
 * Non-archived projects the given user is a member of. `GET /projects` returns
 * every project on the platform regardless of membership, so this fans out one
 * members query per project and keeps the ones listing `userId` — matching the
 * access check the backend enforces on all sub-resources. `active` is the
 * unfiltered non-archived list, for skeletons sized before membership resolves.
 */
export function useMyProjects(userId: string) {
  const { data: projects } = useQuery(projectsListOptions());
  const active = useMemo(() => (projects ?? []).filter((p) => p.archived_at == null), [projects]);
  return useQueries({
    queries: active.map((p) => projectMembersOptions(p.id)),
    combine: (results) => ({
      active,
      mine: active.filter((_, i) =>
        results[i]?.data ? results[i].data.some((m) => m.user_id === userId) : false,
      ),
      isPending: projects === undefined || results.some((r) => r.isPending),
    }),
  });
}
